import React, { useState, useRef, useEffect, useMemo } from 'react';

// "+ Add client" combobox — same search/scroll/pinned-footer shape as
// OfferingTypePicker.js, built standalone rather than shared (no badge here,
// clients don't have one; stays open after a row is clicked so a coach can
// add multiple clients in one pass — closes on click-outside/Escape/re-toggle).
export default function ClientAddPicker({ clients, selectedClientIds, onToggle, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter(c => !q || c.name.toLowerCase().includes(q));
  }, [clients, search]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      searchInputRef.current?.focus();
    }
  }, [open]);

  return (
    <div style={styles.wrapper} ref={wrapperRef}>
      <button type="button" style={styles.addBtn} onClick={() => setOpen(o => !o)}>
        + Add client
      </button>

      {open && (
        <div style={styles.popover}>
          <input
            ref={searchInputRef}
            style={styles.searchInput}
            placeholder="Search clients"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={styles.list}>
            {filtered.map(c => {
              const selected = selectedClientIds.includes(c.id);
              const initials = c.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              return (
                <div key={c.id} style={styles.row} onClick={() => onToggle(c.id)}>
                  <span style={{ ...styles.avatar, background: selected ? 'var(--sage)' : 'var(--mint)', color: selected ? 'white' : 'var(--sage)' }}>
                    {initials}
                  </span>
                  <span style={styles.rowName}>{c.name}</span>
                  {selected && <span style={styles.check}>✓</span>}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={styles.emptyState}>No clients match "{search}".</div>
            )}
          </div>
          <div style={styles.footer} onClick={() => { onCreateNew(); setOpen(false); }}>
            <span style={styles.footerAdd}>+ New client</span>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper: { position: 'relative', display: 'inline-block' },
  addBtn: { padding: '7px 14px', borderRadius: '20px', border: '1px dashed var(--border)', background: 'white', color: 'var(--sage)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  popover: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, width: '300px', zIndex: 50, background: 'white', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', maxHeight: '320px', overflow: 'hidden' },
  searchInput: { width: '100%', padding: '12px 14px', border: 'none', borderBottom: '1px solid var(--border-light)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', flexShrink: 0 },
  list: { overflowY: 'auto', flex: 1 },
  row: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' },
  avatar: { width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10.5px', fontWeight: '600', flexShrink: 0 },
  rowName: { fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', flex: 1 },
  check: { color: 'var(--sage)', fontSize: '13px', fontWeight: '700' },
  emptyState: { padding: '16px 14px', fontSize: '13px', color: 'var(--text-soft)' },
  footer: { padding: '11px 14px', borderTop: '1px solid var(--border-light)', background: 'var(--mint)', cursor: 'pointer', flexShrink: 0 },
  footerAdd: { fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '600', color: 'var(--sage)' },
};
